export type CoachReviewResolvedSource = {
  source: "Transcript" | "Reflection" | "Shared Reflection" | "Artifact" | "Evidence";
  itemId: string | null;
  meetingId: string | null;
};

export type CoachReviewReferenceInput = {
  label: string;
  link: string | null;
  evidenceId?: string | null;
  resolvedSource?: CoachReviewResolvedSource | null;
};

type CoachReviewViewInput = {
  coachId: string;
  leaderId: string;
  openedFrom: string;
};

function normalizeKeyPart(value: string | null | undefined) {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9:/_-]+/g, "");
}

function normalizeSourceKey(source: CoachReviewResolvedSource["source"]) {
  if (source === "Shared Reflection") return "shared-reflection";
  return source.toLowerCase();
}

export function getCoachReviewArtifactKey(input: CoachReviewReferenceInput) {
  if (input.evidenceId) {
    return `evidence_reference:${input.evidenceId}`;
  }

  if (input.resolvedSource?.itemId) {
    return `evidence_reference:${normalizeSourceKey(input.resolvedSource.source)}:${input.resolvedSource.itemId}`;
  }

  const fallbackLink = normalizeKeyPart(input.link);
  if (fallbackLink) {
    return `evidence_reference:link:${fallbackLink}`;
  }

  return `evidence_reference:label:${normalizeKeyPart(input.label)}`;
}

export function buildCoachTranscriptReviewView(
  input: CoachReviewViewInput & { transcriptId: string },
) {
  return {
    coach_id: input.coachId,
    leader_id: input.leaderId,
    artifact_type: "transcript_review" as const,
    artifact_key: `transcript_review:${input.transcriptId}`,
    transcript_upload_id: input.transcriptId,
    meeting_id: null,
    artifact_label: null,
    artifact_link: null,
    opened_from: input.openedFrom,
  };
}

export function buildCoachEvidenceView(
  input: CoachReviewViewInput & { reference: CoachReviewReferenceInput; resolvedSource?: CoachReviewResolvedSource | null },
) {
  const resolvedSource = input.resolvedSource ?? input.reference.resolvedSource ?? null;
  return {
    coach_id: input.coachId,
    leader_id: input.leaderId,
    artifact_type: "evidence_reference" as const,
    artifact_key: getCoachReviewArtifactKey({
      ...input.reference,
      resolvedSource,
    }),
    transcript_upload_id: resolvedSource?.source === "Transcript" ? resolvedSource.itemId : null,
    meeting_id: resolvedSource?.meetingId ?? null,
    artifact_label: input.reference.label,
    artifact_link: input.reference.link,
    opened_from: input.openedFrom,
  };
}

export function isCoachReviewArtifactViewed(
  viewedKeys: Set<string>,
  input: CoachReviewReferenceInput,
) {
  return viewedKeys.has(getCoachReviewArtifactKey(input));
}

export function filterUnopenedCoachReviewTranscripts<
  T extends { id: string; leader_id: string },
>(transcripts: T[], openedTranscriptIds: Set<string>) {
  return transcripts.filter((transcript) => !openedTranscriptIds.has(transcript.id));
}
